import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls' 
import { interpolateViridis } from 'd3-scale-chromatic';
import { resolveObjectURL } from 'buffer';

function hexToRgb(hex: string): { r: number, g: number, b: number } | null {
    // Remove the '#' if it exists
    hex = hex.replace(/^#/, '');

    // Parse the hex value into RGB components
    const bigint = parseInt(hex, 16);
    const r = ((bigint >> 16) & 255) / 255;
    const g = ((bigint >> 8) & 255) / 255;
    const b = (bigint & 255) / 255;

    // Check for valid hex input
    if (isNaN(r) || isNaN(g) || isNaN(b)) {
        return null;
    }

    return { r, g, b };
}

export class PointCloudRenderer {
    private container: HTMLElement;
    private assetsPath: string;
    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private controls: OrbitControls;
    private renderer: THREE.Renderer;
    private points: THREE.Points;
    private depthMin: number;
    private depthMax: number;

    private destroyCallback: (() => void) | null = null;

    constructor(containerElement: HTMLElement, assetsPath: string, camera: THREE.PerspectiveCamera, depthMin: number, depthMax: number) {
        this.container = containerElement;
        this.assetsPath = assetsPath;
        this.camera = camera;
        this.depthMin = depthMin;
        this.depthMax = depthMax;
        this.init();
    }

    private async init(): Promise<void> {
        // Setup the scene
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x000000);

        // Orbit Controls
        this.controls = new OrbitControls(this.camera, this.container);
        this.controls.target = new THREE.Vector3(0, 0, -1);
        this.controls.enableZoom = true;  // allow zoom
        this.controls.enablePan = false;  // disallow panning because for some reason it only works on the current renderer

        await this.loadPoints();

        // Setup the WebGL renderer
        this.renderer = new THREE.WebGLRenderer();
        this.renderer.setSize(256, 256);
        this.container.appendChild(this.renderer.domElement);

        // Start animation loop
        this.animate();
    }

    async destroy(): Promise<void> {
        return new Promise((resolve) => {
            this.destroyCallback = resolve;
        });
    }

    private async loadPoints(): Promise<void> {
        const geometry = new THREE.BufferGeometry();
        
        const sceneName: string = this.container.getAttribute('scene-name') || '';
        const modelName: string = this.container.getAttribute('model-name')!;

        const url = `${this.assetsPath}/bin/${sceneName}/${modelName}.bin`;
        const binary = await fetch(url);
        
        // create a buffer for the positions
        const vertexPositionData = new Float32Array(await binary.arrayBuffer());
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertexPositionData, 3));

        // create a buffer for the colors
        const colorData = new Float32Array(vertexPositionData.length);
        for (let i = 0; i < vertexPositionData.length; i += 3) {
            const depth = vertexPositionData[i + 2];
            const depthNormalized = (depth - this.depthMin) / (this.depthMax - this.depthMin);
            const rgbString = interpolateViridis(depthNormalized);
            const rgb = hexToRgb(rgbString);
            if (rgb == null) {
                throw new Error('Invalid hex color: ' + rgbString);
            }
            colorData[i] = rgb.r;
            colorData[i + 1] = rgb.g;
            colorData[i + 2] = rgb.b;
        }
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colorData, 3));

        const material = new THREE.PointsMaterial({vertexColors: true, size: 0.1});
        this.points = new THREE.Points(geometry, material);
        this.points.rotateY(Math.PI);
        this.points.rotateZ(Math.PI);

        this.scene.add(this.points);
    }

    private animate(): void {
        if (this.destroyCallback != null) {
            this.container.removeChild(this.renderer.domElement);
            this.destroyCallback();
            return;
        }

        this.render();
        requestAnimationFrame(() => this.animate());
    }

    private render(): void {
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }
}

export class JointlyControlledRenderers {
    private container: HTMLElement;
    private renderers: PointCloudRenderer[];

    constructor(
        containerElement: HTMLElement,
        assetsPath: string,
        modelNames: string[],
        fov: number,
        depthMin: number,
        depthMax: number,
        showTitles: boolean,
    ) {
        this.container = containerElement;
        const sceneName: string = this.container.getAttribute('scene-name')!;

        // maybe add the title as a vertical label
        if (showTitles) {
            const titleElement = document.createElement('div');
            titleElement.classList.add('title', 'frame-height');
            titleElement.innerText = sceneName;
            this.container.appendChild(titleElement);
        }

        // load image
        const imagePath: string = `${assetsPath}/images/${sceneName}.jpg`;
        const image: HTMLImageElement = document.createElement('img');
        image.setAttribute('src', imagePath);
        image.classList.add('frame', 'frame-width', 'frame-height');
        this.container.appendChild(image);

        // Setup the camera
        const camera = new THREE.PerspectiveCamera(fov, 1.0, 0.01, 100);
        camera.position.set(0, 0, 0);

        this.renderers = [];
        // Create the containers for the renderers
        for (let modelName of modelNames) {
            const rendererContainer = document.createElement('div');
            rendererContainer.classList.add('frame', 'frame-width', 'frame-height');
            rendererContainer.setAttribute('scene-name', sceneName);
            rendererContainer.setAttribute('model-name', modelName);
            this.renderers.push(new PointCloudRenderer(rendererContainer, assetsPath, camera, depthMin, depthMax));
            this.container.appendChild(rendererContainer);
        }
    }

    public async destroy(): Promise<void> {
        await Promise.all(this.renderers.map((renderer) => renderer.destroy()));
    }
}